import template from '../data/template.json';
import { addDays, diffDays, dow, weekStart } from './dates';
import type { Member, PlanDay, PlanMeal, Portion, Track } from './types';

export interface Dish {
  name: string;
  H: Portion;
  A: Portion;
}
/** A menu: dishes per slot plus a rotation of weeks. web/src/data/template.json is the default. */
export interface Template {
  breakfasts: Record<string, Dish>;
  snacks: Record<string, Dish>;
  mains: Record<string, Dish>;
  cheat: Dish;
  weeks: Record<string, (string | null)[]>[];
  tracks: Record<Track, { kcal: number; protein: number }>;
}
const T = template as unknown as Template;

const DOW_KEY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FISH = new Set(['hamsi', 'palamut', 'levrek', 'somon']);

type Slot = [time: string, dur: number, kind: string, icon: string] | null;

/** Meal times. Tuesday and Friday are training days in the template (evening and morning class). */
function slots(d: number): Slot[] {
  if (d === 2)
    return [['08:00', 20, 'Breakfast', '🍳'], ['10:30', 10, 'Snack', '🥣'], ['13:00', 30, 'Lunch', '🥗'], ['17:45', 10, 'Pre-workout', '🍌'], ['20:30', 30, 'Dinner', '🍽️']];
  if (d === 5)
    return [['08:15', 20, 'Breakfast', '🍳'], ['11:05', 10, 'Post-workout', '🥛'], ['13:00', 30, 'Lunch', '🥗'], ['16:30', 10, 'Snack', '🥣'], ['19:30', 30, 'Dinner', '🍽️']];
  if (d === 6) return [['10:30', 45, 'Brunch', '🍳'], ['15:00', 10, 'Snack', '🥣'], null, null, ['20:00', 90, 'Cheat meal', '🎉']];
  return [['08:00', 20, 'Breakfast', '🍳'], ['10:30', 10, 'Snack', '🥣'], ['13:00', 30, 'Lunch', '🥗'], ['16:00', 10, 'Snack', '🥣'], ['19:30', 30, 'Dinner', '🍽️']];
}

function dish(tpl: Template, key: string, slot: number): Dish {
  const found = key === 'CHEAT' ? tpl.cheat : slot === 0 ? tpl.breakfasts[key] : slot === 1 || slot === 3 ? tpl.snacks[key] : tpl.mains[key];
  if (!found) throw new Error(`Menu has no dish "${key}" for slot ${slot}`);
  return found;
}

function menuFor(tpl: Template, start: string, date: string): (string | null)[] {
  const n = tpl.weeks.length;
  const w = ((Math.floor(diffDays(date, weekStart(start)) / 7) % n) + n) % n; // the day before the start is week −1
  return tpl.weeks[w][DOW_KEY[dow(date)]];
}

function noteFor(slot: number, key: string, d: number, prevDinner: string | null, nextLunch: string | null): string {
  if (key === 'CHEAT') return 'Drink 500 ml water first. Eat slowly, stop at satisfied, walk after. Back on plan at Sunday breakfast; no skipping meals to make up for it.';
  if (slot === 3 && key === 'prep') return 'Training tonight: eat this 60–90 minutes before class.';
  if (slot === 1 && d === 5) return 'Right after your morning class.';
  if (slot === 2) {
    if (key === prevDinner) return 'Leftovers from last night (cooked double). Pack it if you are at work.';
    if (key === 'tavuk_bulgur') return 'From Sunday meal prep. Lunchbox if you are at work.';
    if (d === 5) return 'Post-workout lunch: fresh and quick.';
    if (d === 0) return 'Fresh lunch. Soup keeps 3 days, so make a big pot.';
    return 'Lunchbox if you are at work.';
  }
  if (slot === 4 && d !== 6) {
    const parts: string[] = [];
    if (key === 'durum') parts.push('Use the chicken grilled at Sunday prep. A 10-minute dinner after class.');
    else if (key === nextLunch) parts.push("Cook double: the second half is tomorrow's lunch.");
    if (FISH.has(key)) parts.push('Fish night: buy it fresh today.');
    return parts.join(' ');
  }
  return '';
}

/** Scale a template portion for someone whose target differs from the template track. */
export function portionFor(dishPortion: Dish, member: Pick<Member, 'track' | 'targets'>, tpl: Template = T): Portion {
  const track = member.track ?? 'H';
  const base = dishPortion[track];
  const kcal = member.targets?.kcal;
  if (!kcal) return base;
  const scale = kcal / tpl.tracks[track].kcal;
  if (Math.abs(scale - 1) < 0.06) return base;
  return { d: `Portions ×${scale.toFixed(2)}: ${base.d}`, k: Math.round(base.k * scale), p: Math.round(base.p * scale) };
}

/** Build a run of plan days from a menu template (the 4-week default unless one is given). */
export function buildPlanDays(start: string, days: number, members: Member[], planId: string, tpl: Template = T): PlanDay[] {
  const out: PlanDay[] = [];
  for (let i = 0; i < days; i++) {
    const date = addDays(start, i);
    const d = dow(date);
    const menu = menuFor(tpl, start, date);
    const prevDinner = menuFor(tpl, start, addDays(date, -1))[4];
    const nextLunch = menuFor(tpl, start, addDays(date, 1))[2];
    const sl = slots(d);
    const meals: PlanMeal[] = [];
    menu.forEach((key, slot) => {
      const s = sl[slot];
      if (!key || !s) return;
      const dsh = dish(tpl, key, slot);
      const portions: Record<string, Portion> = {};
      for (const m of members) portions[m.uid] = portionFor(dsh, m, tpl);
      meals.push({
        key: `meal-${slot}`,
        time: s[0],
        dur: s[1],
        kind: s[2],
        icon: s[3],
        name: dsh.name.replace(' 🎉', ''),
        note: noteFor(slot, key, d, prevDinner, nextLunch),
        cheat: key === 'CHEAT',
        portions,
      });
    });
    const tags: string[] = [];
    if (d === 0) tags.push('Meal prep');
    if (d === 5) tags.push('Weigh-in');
    if (d === 6) tags.push('Cheat meal');
    out.push({ date, dow: d, week: Math.floor(diffDays(date, weekStart(start)) / 7) + 1, planId, tags, meals });
  }
  return out;
}

export function dayTotals(day: PlanDay, uid: string): { k: number; p: number } {
  return day.meals.reduce((a, m) => ({ k: a.k + (m.portions[uid]?.k ?? 0), p: a.p + (m.portions[uid]?.p ?? 0) }), { k: 0, p: 0 });
}
